<script setup lang="ts">
/**
 * CoreTextField
 *
 * Opinionated text input field built from:
 * - `CoreField` wrapper (label/description/errors/a11y wiring)
 * - `<input type="text">` control with stable variant/density styling
 *
 * Validation behavior:
 * - `errorMessages` (external/manual) always takes precedence when present.
 * - `rules` are evaluated locally and shown after blur / after form submit.
 * - When rendered inside `CoreForm`, the field registers so the form can run
 *   `validate()` and gate submit.
 */
import { computed, inject, onBeforeUnmount, onMounted, ref, toRef, watch } from 'vue'
import CoreField from '../CoreField.vue'
import type { CoreErrorMessages, CoreFieldDensity, CoreFieldVariant, CoreRule } from '../types'
import { coreFormKey } from '../keys'
import { getCoreControlClasses } from '../controlClasses'
import { normalizeErrorMessages, useRulesValidation } from '../useRulesValidation'
import { useStableId } from '../useStableId'

const props = withDefaults(
  defineProps<{
    modelValue: string
    type?: string
    label?: string
    placeholder?: string
    description?: string
    errorMessages?: CoreErrorMessages
    rules?: CoreRule<string>[]
    variant?: CoreFieldVariant
    density?: CoreFieldDensity
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    name?: string
    id?: string
    hideDetails?: 'auto' | 'always' | 'never'
    debounce?: number
    clearable?: boolean
  }>(),
  {
    label: undefined,
    type: 'text',
    placeholder: undefined,
    description: undefined,
    errorMessages: undefined,
    rules: undefined,
    variant: 'outlined',
    density: 'default',
    disabled: false,
    readonly: false,
    required: false,
    name: undefined,
    id: undefined,
    hideDetails: 'auto',
    debounce: 0,
    clearable: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const form = inject(coreFormKey, null)
const touched = ref(false)

const stableId = useStableId({
  id: toRef(props, 'id'),
  name: toRef(props, 'name'),
  prefix: 'core-field',
})

const inputRef = ref<HTMLInputElement | null>(null)
const inputValue = ref(props.modelValue)
const pendingValue = ref<string | null>(null)
let debounceTimer: number | null = null

watch(
  () => props.modelValue,
  (next) => {
    if (next !== inputValue.value) inputValue.value = next
  },
)

const valueRef = computed({
  get: () => inputValue.value,
  set: (next) => {
    inputValue.value = next
    emit('update:modelValue', next)
  },
})

const { ruleErrors, validateRules, resetRules } = useRulesValidation({
  value: valueRef,
  rules: toRef(props, 'rules'),
})

const externalErrors = computed(() => normalizeErrorMessages(props.errorMessages))
const hasExternalErrors = computed(() => externalErrors.value.length > 0)

const canShowErrors = computed(() => touched.value || form?.submitted.value)

const activeErrors = computed<string[]>(() => {
  if (!canShowErrors.value) return []
  if (hasExternalErrors.value) return externalErrors.value
  return ruleErrors.value
})

const invalid = computed(() => activeErrors.value.length > 0)
const controlClass = computed(() =>
  getCoreControlClasses({ variant: props.variant, density: props.density, invalid: invalid.value }),
)
const clearableVisible = computed(
  () => props.clearable && inputValue.value.length > 0 && !props.disabled && !props.readonly,
)
const inputClass = computed(() => [controlClass.value, clearableVisible.value ? 'pr-9' : ''])

const validateCurrent = async () => {
  touched.value = true
  if (props.disabled) {
    resetRules()
    return []
  }
  if (hasExternalErrors.value) return externalErrors.value
  return await validateRules()
}

const resetValidation = () => {
  touched.value = false
  resetRules()
}

let unregister: (() => void) | null = null
onMounted(() => {
  if (!form) return
  const key = props.name || stableId.value
  unregister = form.registerField({
    key,
    validate: validateCurrent,
    resetValidation,
  })
})

onBeforeUnmount(() => unregister?.())

const emitDebounced = (next: string) => {
  if (!props.debounce || props.debounce <= 0) {
    emit('update:modelValue', next)
    return
  }
  pendingValue.value = next
  if (debounceTimer) window.clearTimeout(debounceTimer)
  debounceTimer = window.setTimeout(() => {
    emit('update:modelValue', pendingValue.value ?? next)
    pendingValue.value = null
    debounceTimer = null
  }, props.debounce)
}

const flushDebounce = () => {
  if (!debounceTimer) return
  window.clearTimeout(debounceTimer)
  debounceTimer = null
  emit('update:modelValue', inputValue.value)
  pendingValue.value = null
}

const onBlur = async () => {
  flushDebounce()
  await validateCurrent()
}

const onInput = async (event: Event) => {
  const target = event.target as HTMLInputElement
  inputValue.value = target.value
  emitDebounced(target.value)

  if (canShowErrors.value && !hasExternalErrors.value) {
    await validateRules()
  }
}

const onClear = async () => {
  if (!clearableVisible.value) return
  inputValue.value = ''
  if (debounceTimer) {
    window.clearTimeout(debounceTimer)
    debounceTimer = null
  }
  emit('update:modelValue', '')
  pendingValue.value = null
  inputRef.value?.focus()
  if (canShowErrors.value && !hasExternalErrors.value) {
    await validateRules()
  }
}

onBeforeUnmount(() => {
  if (debounceTimer) window.clearTimeout(debounceTimer)
})
</script>

<template>
  <CoreField
    :id="stableId"
    :name="name"
    :label="label"
    :description="description"
    :error-messages="activeErrors"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
    :variant="variant"
    :density="density"
    :hide-details="hideDetails"
  >
    <template #default="{ id: controlId, describedBy, invalid: slotInvalid, disabled: slotDisabled, readonly: slotReadonly, required: slotRequired }">
      <div class="relative">
        <input
          ref="inputRef"
          :id="controlId"
          :type="type"
          :name="name"
          :value="inputValue"
          :placeholder="placeholder"
          :disabled="slotDisabled"
          :readonly="slotReadonly"
          :required="slotRequired"
          :aria-invalid="slotInvalid ? 'true' : undefined"
          :aria-describedby="describedBy"
          :class="inputClass"
          @input="onInput"
          @blur="onBlur"
        />
        <button
          v-if="clearableVisible"
          type="button"
          class="absolute inset-y-0 right-2 flex items-center text-on-surface-variant/70 hover:text-on-surface"
          aria-label="Clear"
          @click="onClear"
        >
          ×
        </button>
      </div>
    </template>
  </CoreField>
</template>
